import { useCallback, useEffect, useRef, useState } from 'react'
import {
  getPurchaseScratchDonorHistory,
  purchaseScratchPrizeLabel,
  purchaseScratchPrizeStatus,
  scratchError,
  type PurchaseScratchAttempt,
  type PurchaseScratchDonorHistory,
} from '@/lib/purchase-scratch'
import { formatDateTime } from './landing-profile-format'
import styles from '@/components/PurchaseScratchCard/PurchaseScratchCard.module.css'

export function PurchaseScratchHistory({
  campaignId,
  onView,
}: {
  campaignId: number
  onView: (card: PurchaseScratchAttempt) => void
}) {
  const [history, setHistory] = useState<PurchaseScratchDonorHistory | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const controller = useRef<AbortController | null>(null)
  const loadingRef = useRef(false)
  const mounted = useRef(false)
  const nextPage = useRef(1)

  const load = useCallback(
    async (page: number) => {
      if (loadingRef.current) return
      loadingRef.current = true
      const request = new AbortController()
      controller.current = request
      setLoading(true)
      setError('')
      try {
        const data = await getPurchaseScratchDonorHistory(
          campaignId,
          page,
          request.signal
        )
        if (request.signal.aborted || !mounted.current) return
        nextPage.current = data.page + 1
        setHistory((current) => ({
          ...data,
          items:
            page === 1
              ? data.items
              : [
                  ...new Map(
                    [...(current?.items ?? []), ...data.items].map((card) => [
                      card.id,
                      card,
                    ])
                  ).values(),
                ],
        }))
      } catch (cause) {
        if (!request.signal.aborted && mounted.current) setError(scratchError(cause))
      } finally {
        if (controller.current === request) {
          loadingRef.current = false
          if (mounted.current) setLoading(false)
        }
      }
    },
    [campaignId]
  )

  useEffect(() => {
    mounted.current = true
    void load(1)
    return () => {
      mounted.current = false
      controller.current?.abort()
      loadingRef.current = false
    }
  }, [load])

  return (
    <section
      aria-label="Histórico completo de raspadinhas"
      className={styles.donorHistory}
    >
      <h3>Meu histórico de raspadinhas</h3>
      {history && (
        <p>
          {history.total}{' '}
          {history.total === 1 ? 'raspadinha revelada' : 'raspadinhas reveladas'} ·{' '}
          {history.items.length} exibidas
        </p>
      )}
      <div className={styles.historyList}>
        {history?.items.map((card) => (
          <button
            type="button"
            key={card.id}
            className={`${styles.historyItem} ${styles.donorHistoryItem}`}
            onClick={() => onView(card)}
          >
            <span className={styles.historyResult} data-won={card.won}>
              <strong>{purchaseScratchPrizeLabel(card)}</strong>
              <span>{purchaseScratchPrizeStatus(card)}</span>
            </span>
            <span>
              <time dateTime={card.revealed_at ?? card.created_at}>
                {formatDateTime(card.revealed_at ?? card.created_at)}
              </time>
              <small>
                Raspadinha #{card.id} · Compra #{card.payment_id}
              </small>
            </span>
            <small>Ver resultado</small>
          </button>
        ))}
      </div>
      {loading && <p role="status">Carregando histórico de raspadinhas…</p>}
      {error && (
        <div role="alert">
          <p>{error}</p>
          <button
            type="button"
            className={styles.quietButton}
            disabled={loading}
            onClick={() => void load(nextPage.current)}
          >
            Tentar novamente
          </button>
        </div>
      )}
      {!loading && !error && history?.total === 0 && (
        <p>Você ainda não revelou nenhuma raspadinha.</p>
      )}
      {!loading && !error && history && history.page < history.last_page && (
        <button
          type="button"
          className={styles.quietButton}
          onClick={() => void load(nextPage.current)}
        >
          Carregar mais raspadinhas
        </button>
      )}
    </section>
  )
}
